import { useEffect, useMemo, useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { ZONA_HORARIA, grupoDe, hoy, type GrupoEvento } from '@abastece/shared';
import { db } from '../db/dexie.js';
import { api } from '../sync/client.js';
import { SIN_FILTROS_BITACORA, eventoCoincide, filtrosDeBitacora, palabrasDelEvento, type FiltroBitacora } from '../lib/bitacora.js';
import { rangoDe } from '../lib/historial.js';
import { leerBitacora, quedanMasViejos } from '../lib/leerBitacora.js';
import { usePuede } from '../lib/sesion.js';
import { BuscarEnLista, FiltroFechas, FiltrosEnLinea } from '../ui/FiltrosHistorial.js';
import { DetalleEvento } from '../ui/DetalleEvento.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { Ico } from '../ui/Iconos.js';
import { localeActual, useT, type Clave } from '../i18n/index.js';
import { etiquetaEventoIdioma } from '../i18n/dominio.js';

type Filtro = GrupoEvento | 'todo';

const GRUPOS: { clave: Filtro; nombre: Clave }[] = [
  { clave: 'todo', nombre: 'bitacora.grupo.todo' },
  { clave: 'ventas', nombre: 'bitacora.grupo.ventas' },
  { clave: 'inventario', nombre: 'bitacora.grupo.inventario' },
  { clave: 'clientes', nombre: 'bitacora.grupo.clientes' },
  { clave: 'dinero', nombre: 'bitacora.grupo.dinero' },
  { clave: 'sistema', nombre: 'bitacora.grupo.sistema' },
];

interface RechazoServidor {
  id: number;
  eventoId: string;
  tipo: string;
  motivo: string;
  creadoEn: string;
  revisado: boolean;
}

const hora = (iso: string) =>
  new Date(iso).toLocaleString(localeActual(), {
    timeZone: ZONA_HORARIA,
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

export function Bitacora() {
  const t = useT();
  const puedeVer = usePuede('ver_bitacora');

  const { vista: vistaPedida, filtro: filtroPedido } = useSearch({ from: '/bitacora' });
  const [vista, setVista] = useState<'movimientos' | 'rechazos'>(vistaPedida ?? 'movimientos');
  const [filtro, setFiltro] = useState<Filtro>(filtroPedido ?? 'todo');
  const [limite, setLimite] = useState(100);
  /** «Ver más» ran past the latest fifteen hundred: the rest of the log is read, as a search reads it. */
  const [masViejos, setMasViejos] = useState(false);
  const [rechazos, setRechazos] = useState<RechazoServidor[] | null>(null);
  /** The record opened, whole (the owner, 2026-10-12: «for each item can we have it be clickable»). */
  const [abierto, setAbierto] = useState<string | null>(null);
  const [errorRed, setErrorRed] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState<FiltroBitacora>(SIN_FILTROS_BITACORA);
  const rango = rangoDe(busqueda.periodo, hoy(), busqueda);
  const puestos = filtrosDeBitacora(busqueda, rango);
  const conTexto = busqueda.texto.trim() !== '';
  /** Anything asked of it: then it looks through the whole log, not only the latest. */
  const buscando = conTexto || puestos.length > 0;
  const buscar = (cambio: Partial<FiltroBitacora>) => {
    setBusqueda((b) => ({ ...b, ...cambio }));
    setLimite(100);
  };

  /**
   * Read from the local log, not the server.
   *
   * The device already holds every event it has pulled, so the audit trail works with no signal
   * — same as everything else here. A screen that needs the network to answer "what happened on
   * the 14th" is a screen that is unavailable exactly when somebody is trying to find money.
   *
   * Two reads, because the serverSeq index only holds what the server has numbered. What this
   * device recorded and has not sent yet — a sale made with no signal, a wrong PIN at the lock
   * screen — would otherwise be missing from the one screen that exists to show it. Those go
   * first, newest on top, marked as not synced.
   *
   * The latest fifteen hundred, while nothing is asked of it; the whole log once something is, or
   * once «Ver más» runs past them (the owner, 2026-10-12, of a list that stopped short).
   */
  const todo = buscando || masViejos;
  const eventos = useLiveQuery(() => leerBitacora(todo), [todo], []);
  const usuarios = useLiveQuery(() => db.usuarios.toArray(), [], []);
  const nombreDe = (id: string) => usuarios.find((u) => u.id === id)?.nombre;

  const encontrados = useMemo(
    () =>
      eventos.filter(
        (e) =>
          (filtro === 'todo' || grupoDe(e.type) === filtro) &&
          (!buscando || eventoCoincide(e, busqueda, rango, etiquetaEventoIdioma(e.type, e.payload), nombreDe(e.actorId))),
      ),
    // `rango` is worked out afresh each time: its two days are what it is.
    [eventos, filtro, buscando, busqueda, rango.desde, rango.hasta, usuarios],
  );
  const visibles = encontrados.slice(0, limite);
  /** Only the latest were read, and the log goes back further: a group picked above among them too. */
  const cortada = useMemo(() => !todo && quedanMasViejos(eventos), [todo, eventos]);
  // A group with none among the latest looks further back by itself: «Sin movimientos» over a log
  // that has some, older, would be the list stopping short again.
  useEffect(() => {
    if (cortada && encontrados.length === 0) setMasViejos(true);
  }, [cortada, encontrados.length]);

  // What the server refused; not what this device's own books refused as it was done, which the
  // person saw refused then (`rechazadoAqui`), and which stays in the list above.
  const rechazadosLocales = eventos.filter((e) => e.estado === 'rechazado' && !e.rechazadoAqui);
  /** Of what the server refused, what this device holds, and so can open: what it sent itself. */
  const rechazosAqui = useLiveQuery(
    async () => new Set((await db.eventos.bulkGet((rechazos ?? []).map((r) => r.eventoId))).flatMap((e) => (e ? [e.id] : []))),
    [rechazos],
    new Set<string>(),
  );

  useEffect(() => {
    if (vista !== 'rechazos') return;
    let vivo = true;
    void api
      .rechazos(true)
      .then((r) => vivo && setRechazos(r.rechazos))
      .catch(
        (e: unknown) => vivo && setErrorRed(e instanceof Error ? e.message : t('sync.sinConexion')),
      );
    return () => {
      vivo = false;
    };
  }, [vista]);

  async function marcar(id: number) {
    await api.marcarRevisado(id);
    setRechazos((rs) => rs?.map((r) => (r.id === id ? { ...r, revisado: true } : r)) ?? null);
  }

  if (!puedeVer) {
    return (
      <Pagina titulo={t('bitacora.titulo')}>
        <Vacio mensaje={t('comun.esDelDueno')} />
      </Pagina>
    );
  }

  const sinRevisar = rechazos?.filter((r) => !r.revisado) ?? [];

  return (
    <Pagina
      titulo={t('bitacora.titulo')}
      subtitulo={t('bitacora.registros', { n: eventos.length })}
      volverA="/ajustes"
      ancho="amplio"
    >
      <div className="mb-4 flex gap-2 border-b border-slate-800">
        {(['movimientos', 'rechazos'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setVista(v)}
            data-sin-icono="pestana"
            className={`-mb-px border-b-2 px-3 py-2 text-sm transition ${
              vista === v
                ? 'border-marca-500 font-medium text-slate-100'
                : 'border-transparent text-slate-500 hover:border-slate-500 hover:text-slate-300'
            }`}
          >
            {t(v === 'movimientos' ? 'bitacora.movimientos' : 'bitacora.rechazos')}
            {v === 'rechazos' && (sinRevisar.length || rechazadosLocales.length) > 0 && (
              <span className="ml-2 rounded-full bg-rose-500/20 px-2 py-0.5 text-xs text-rose-300">
                {sinRevisar.length || rechazadosLocales.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {vista === 'movimientos' && (
        <>
          <div className="mb-4 flex flex-wrap gap-2">
            {GRUPOS.map((g) => (
              <button
                key={g.clave}
                type="button"
                onClick={() => setFiltro(g.clave)}
                data-sin-icono="pestana"
                className={`rounded-lg px-3 py-1.5 text-sm transition ${
                  g.clave === filtro ? 'bg-marca-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-slate-100'
                }`}
              >
                {t(g.nombre)}
              </button>
            ))}
          </div>

          <FiltrosEnLinea
            buscar={{ texto: busqueda.texto, onTexto: (texto) => buscar({ texto }), placeholder: t('bitacora.buscar') }}
            hayPuestos={puestos.length > 0}
            onQuitarTodos={() => buscar({ ...SIN_FILTROS_BITACORA, texto: busqueda.texto })}
          >
            <FiltroFechas periodo={busqueda.periodo} desde={busqueda.desde} hasta={busqueda.hasta} onCambio={buscar} />
            <BuscarEnLista
              clave="quien"
              etiqueta={t('bitacora.quien')}
              placeholder={t('turnosHist.buscarQuien')}
              opciones={usuarios}
              value={busqueda.quien}
              onChange={(quien) => buscar({ quien })}
            />
          </FiltrosEnLinea>

          {visibles.length === 0 ? (
            buscando ? (
              <Vacio mensaje={t('bitacora.ninguno')} icono="buscar">
                <button
                  type="button"
                  className="btn-secundario"
                  onClick={() => {
                    buscar(SIN_FILTROS_BITACORA);
                    setFiltro('todo');
                  }}
                >
                  <Ico icono="reiniciar" />
                  {t('historial.quitarBusqueda')}
                </button>
              </Vacio>
            ) : (
              <Vacio mensaje={t('bitacora.sinMovimientos')} />
            )
          ) : (
            <ul className="flex flex-col gap-1">
              {visibles.map((e) => (
                <li key={e.id} data-bitacora-evento={e.id}>
                  <button
                    type="button"
                    data-sin-icono="fila"
                    onClick={() => setAbierto(e.id)}
                    className={`tarjeta-accion flex w-full items-start justify-between gap-3 py-2.5 text-left ${
                      e.estado === 'rechazado' ? 'ring-rose-500/30' : ''
                    }`}
                  >
                    <span className="min-w-0">
                      <span
                        className={`block truncate text-sm font-medium ${
                          e.type === 'sesion.pin_incorrecto' ? 'text-rose-300' : ''
                        }`}
                      >
                        {etiquetaEventoIdioma(e.type, e.payload)}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-slate-500">
                        {usuarios.find((u) => u.id === e.actorId)?.nombre ??
                          t('bitacora.desconocido')}{' '}
                        · {hora(e.clientTs)}
                        {e.estado === 'pendiente' && t('bitacora.sinSincronizar')}
                      </span>
                      {/* Searching, its own words: what the box may have found it by, a ticket or a reason. */}
                      {conTexto && palabrasDelEvento(e.payload).length > 0 && (
                        <span className="mt-0.5 block text-xs wrap-break-word text-slate-400" data-bitacora-palabras>
                          {palabrasDelEvento(e.payload).join(' · ')}
                        </span>
                      )}
                      {e.motivoRechazo && (
                        <span className="mt-1 block text-xs text-rose-300">{e.motivoRechazo}</span>
                      )}
                    </span>
                    <span className="tabular shrink-0 text-xs text-slate-600">
                      {e.serverSeq ? `#${e.serverSeq}` : '—'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {(encontrados.length > limite || cortada) && (
            <button
              type="button"
              className="btn-secundario mt-4 w-full text-sm"
              onClick={() => {
                setLimite((n) => n + 200);
                if (cortada && encontrados.length < limite + 200) setMasViejos(true);
              }}
              data-bitacora-mas
            >
              <Ico icono="desplegar" />
              {t('comun.verMas')}
            </button>
          )}

          <p className="mt-4 text-xs text-slate-600">
            {t('bitacora.explicacion')}
          </p>
        </>
      )}

      {vista === 'rechazos' && (
        <>
          {errorRed && (
            <p className="mb-4 text-sm text-amber-300">
              {t('bitacora.errorServidor', { error: errorRed })}
            </p>
          )}

          {(rechazos ?? []).length === 0 && rechazadosLocales.length === 0 ? (
            <Vacio mensaje={t('bitacora.sinRechazos')} />
          ) : (
            <ul className="flex flex-col gap-2">
              {(rechazos ?? []).map((r) => {
                const lo = (
                  <>
                    <span className="block truncate font-medium">{etiquetaEventoIdioma(r.tipo)}</span>
                    <span className="mt-0.5 block text-xs text-slate-500">{hora(r.creadoEn)}</span>
                    <span className="mt-1 block text-sm text-rose-300">{r.motivo}</span>
                  </>
                );
                return (
                  <li
                    key={r.id}
                    className={`tarjeta flex items-start justify-between gap-3 ${r.revisado ? 'opacity-50' : 'ring-rose-500/30'}`}
                  >
                    {/* What another device sent is not on this one: there is nothing more to open. */}
                    {rechazosAqui.has(r.eventoId) ? (
                      <button
                        type="button"
                        data-sin-icono="fila"
                        className="tarjeta-abrir min-w-0 flex-1 text-left"
                        onClick={() => setAbierto(r.eventoId)}
                      >
                        {lo}
                      </button>
                    ) : (
                      <div className="min-w-0">{lo}</div>
                    )}
                    {!r.revisado && (
                      <button
                        type="button"
                        className="btn-secundario shrink-0 gap-1 px-3 text-xs"
                        onClick={() => void marcar(r.id)}
                      >
                        <Ico icono="marca" className="size-3.5" />
                        {t('bitacora.revisado')}
                      </button>
                    )}
                  </li>
                );
              })}

              {rechazos === null &&
                rechazadosLocales.map((e) => (
                  <li key={e.id}>
                    <button
                      type="button"
                      data-sin-icono="fila"
                      className="tarjeta-accion w-full text-left ring-rose-500/30"
                      onClick={() => setAbierto(e.id)}
                    >
                      <span className="block font-medium">{etiquetaEventoIdioma(e.type)}</span>
                      <span className="mt-0.5 block text-xs text-slate-500">{hora(e.clientTs)}</span>
                      <span className="mt-1 block text-sm text-rose-300">{e.motivoRechazo}</span>
                    </button>
                  </li>
                ))}
            </ul>
          )}

          <p className="mt-4 text-xs text-slate-600">
            {t('bitacora.explicacionRechazos')}
          </p>
        </>
      )}

      {abierto && <DetalleEvento eventoId={abierto} onCerrar={() => setAbierto(null)} />}
    </Pagina>
  );
}
